import React, { useState, Component } from 'react';
/**
 * ExplanationContent — Conversational explanation with Djay intros.
 *
 * Shows distractor explanation (why wrong) and main explanation (how to solve)
 * with Djay avatar intros and numbered solution steps.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { DjayAvatar, useDjaySelection, getDjayDistractorIntro, getDjayExplanationIntro } from '../../../mascot';
import { ExplanationSteps } from './ExplanationSteps';
// ── Types ────────────────────────────────────────────────────
interface ExplanationContentProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  solutionSteps?: string[];
}
// ── Component ────────────────────────────────────────────────
export function ExplanationContent({
  explanation,
  distractorExplanation,
  isCorrect,
  solutionSteps
}: ExplanationContentProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  // useState initializer runs once — prevents random re-pick on scroll/re-render
  const [distractorIntro] = useState(() => getDjayDistractorIntro());
  const [explanationIntro] = useState(() => getDjayExplanationIntro());
  return <div className="space-y-5" data-id="element-8012">
      {/* Distractor explanation (why it was wrong) */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-8013">
          <div className="flex items-start gap-3 mb-4" data-id="element-8014">
            <motion.div className="flex-shrink-0 mt-0.5" animate={{
          rotate: [0, -5, 5, 0]
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} data-id="element-8015">
              <DjayAvatar size="sm" emotion="thinking" variant={selectedVariant} animate={true} data-id="element-8016" />
            </motion.div>
            <div className="min-w-0 flex-1" data-id="element-8017">
              <h4 className="font-bold text-slate-700 text-sm mb-2" data-id="element-8018">
                {distractorIntro}
              </h4>
              <div className="text-slate-600 text-sm leading-relaxed" data-id="element-8019">
                <LatexRenderer content={distractorExplanation} data-id="element-8020" />
              </div>
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-5" data-id="element-8021" />
        </motion.div>}

      {/* Main explanation (how to solve it) */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: distractorExplanation ? 0.15 : 0,
      duration: 0.3
    }} className="relative" data-id="element-8022">
        <div className="flex items-center gap-2 mb-3" data-id="element-8023">
          <motion.div animate={{
          y: [0, -2, 0]
        }} transition={{
          delay: 0.4,
          duration: 1.5,
          repeat: Infinity,
          repeatType: 'reverse'
        }} data-id="element-8024">
            <DjayAvatar size="sm" emotion="pointing" variant={selectedVariant} animate={true} data-id="element-8025" />
          </motion.div>
          <span className="text-sm font-bold text-orange-600" data-id="element-8026">
            {explanationIntro}
          </span>
        </div>

        <ExplanationSteps text={explanation || (isCorrect ? 'Goed gedaan! Je hebt de juiste strategie gebruikt.' : 'Kijk goed naar de stappen hierboven.')} steps={solutionSteps} variant="conversational" data-id="element-8027" />
      </motion.div>
    </div>;
}